/**
 * Performs an HTTP POST request.
 *
 * @param {Object} params - The parameters for the HTTP POST request.
 * @param {string} params.path - The URL path where the request is sent.
 * @param {object} params.payload - The payload to be sent with the request, as an object.
 * @returns {Promise<Response>} - A promise that resolves to the response of the HTTP POST request.
 * @throws {Error} - Throws an error if the request fails (response.ok is false).
 *
 * @example
 * await httpPost({
 *   path: 'https://example.com/api/data',
 *   payload: { key: 'value' }
 * });
 */
export async function httpPost({
  path,
  payload
}: {
  path: string;
  payload: object;
}): Promise<Response> {
  const body = JSON.stringify(payload);
  const response = await fetch(path, {
    method: 'POST',
    body,
    headers: {
      'content-type': 'application/json'
    }
  });
  if (!response.ok) {
    throw new Error('failed to post to ' + path);
  }
  return response;
}

type GetPath = string | (() => string);

export async function fetchFromMeService<T = any>(
  event: { node: { req: { headers: any } } },
  getPath: GetPath,
  options?: { method: any; body: any }
): Promise<T> {
  const reqHeaders = event.node.req.headers; // Assuming `event.req.headers` is the correct way to access headers in your setup

  // Find the Authentication cookie
  const authCookie = reqHeaders.cookie
    ?.split(';')
    .find((cookie: string) => cookie.trim().startsWith('Authentication='));

  if (!authCookie) {
    throw new Error('Authentication cookie not found');
  }
  const authValue = authCookie.split('=')[1];
  let accessToken;
  try {
    const parsedAuth = JSON.parse(decodeURIComponent(authValue));
    accessToken = parsedAuth.accessToken;
  } catch (error) {
    const { captureException } = useSentry();
    captureException(error as Error, {
      context: 'parseAuthenticationCookie'
    });
    throw new Error('Invalid Authentication cookie format');
  }
  if (!accessToken) {
    throw new Error('Access token not found in Authentication cookie');
  }

  const runtime = useRuntimeConfig();

  const url = `${runtime.public.meService}${typeof getPath === 'function' ? getPath() : getPath}`;

  const fetchOptions = {
    method: options?.method || 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: options?.body ? JSON.stringify(options.body) : null
  };

  const response = await fetch(url, fetchOptions);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch from ${url} (${response.status} ${response.statusText})`
    );
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json() as Promise<T>;
  }

  return response.text() as Promise<T>;
}
